import { HttpErrorResponse, HttpHandler, HttpRequest } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { Router } from '@angular/router';
import { throwError } from 'rxjs';
import { catchError } from 'rxjs/operators';
import { CommonServiceService } from '../common-service/common-service.service';
import { environment } from '../../../../environments/environment';
import {
  clearStoredAccessTokens,
  getStoredAccessToken,
  shouldKeepRequestAuthHeader,
} from '../../auth-session.util';
import { resolveApiLanguage } from '../../utils/client-language.util';

@Injectable({
  providedIn: 'root'
})
export class HeaderInterceptorService {
  constructor(
    private readonly router: Router,
    private readonly commonService: CommonServiceService,
  ) {}

  intercept(request: HttpRequest<any>, next: HttpHandler) {
    /** Snapshot before clone; guest 401s must not open the global login UI. */
    const hadSession = getStoredAccessToken().length > 0;
    const authedRequest = this.cloneWithSessionHeaders(request);

    return next.handle(authedRequest).pipe(
      catchError((error: HttpErrorResponse) => {
        if (error.status === 401) {
          this.handleUnauthorized(request.url, hadSession);
        }
        return throwError(() => error);
      }),
    );
  }

  private handleUnauthorized(requestUrl: string, hadSession: boolean): void {
    if (shouldKeepRequestAuthHeader(requestUrl)) {
      return;
    }
    const isMembershipRoute = this.router.url.includes('/membership');
    const isDashboardRoute = this.router.url.includes('/my-profile-dashboard');
    if (isMembershipRoute || isDashboardRoute) {
      return;
    }
    if (!hadSession) {
      return;
    }
    clearStoredAccessTokens();
    this.commonService.login_subject.next(true);
    this.commonService.is_login_success.next(false);
  }

  private cloneWithSessionHeaders(request: HttpRequest<any>): HttpRequest<any> {
    const headerLang = this.resolveHeaderLang();
    const eventId =
      globalThis.window?.sessionStorage?.getItem('event_id') ?? 'null';
    const baseHeaders = {
      tenant: environment.tenant_Name,
      language: headerLang,
      'Accept-Language': headerLang,
      event_id: eventId,
      source: 'web',
    };

    if (this.shouldPreserveRequestAuth(request)) {
      return request.clone({ setHeaders: baseHeaders });
    }

    const token = getStoredAccessToken();
    if (token) {
      return request.clone({
        setHeaders: { ...baseHeaders, auth: `Bearer ${token}` },
      });
    }

    return request.clone({
      headers: request.headers.delete('auth'),
      setHeaders: baseHeaders,
    });
  }

  private shouldPreserveRequestAuth(request: HttpRequest<any>): boolean {
    if (!shouldKeepRequestAuthHeader(request.url)) {
      return false;
    }
    const auth = request.headers.get('auth')?.trim() ?? '';
    return auth.length > 0;
  }

  private resolveHeaderLang(): string {
    return resolveApiLanguage();
  }
}
